import { useEffect, useState } from 'react';
import { usePushSubscription } from '@/hooks/usePushSubscription';

const PROMPT_FLAG = 'ss_push_prompt_shown';

interface PushPermissionPromptProps {
  /** Tenant slug (used to namespace the subscription server-side). */
  slug: string;
}

/**
 * PushPermissionPrompt — first-visit opt-in card for Web Push.
 *
 * Visibility rules:
 *   - Only mounts UI if the browser actually supports push.
 *   - Only shows if `Notification.permission === 'default'` (never re-prompts
 *     after a denial — the OS owns that flow from then on).
 *   - localStorage flag `ss_push_prompt_shown` is set on either button so
 *     we don't nag the user every visit.
 *
 * Copy is locked per Chunk 8 spec (friendly + sets expectations + escape hatch).
 */
export function PushPermissionPrompt({ slug }: PushPermissionPromptProps): JSX.Element | null {
  const { pushPermissionState, isBusy, error, subscribeUser } = usePushSubscription(slug);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Show whenever we need a USER-GESTURE subscribe — that means either:
    //   (a) permission still 'default' and the user hasn't dismissed before
    //   (b) permission 'granted' but no Worker-side subscription exists
    //       (this catches iOS's "permission carried over but subscribe needs
    //       a real tap" failure mode that's bitten us several times tonight).
    // The LS dismissal flag is honored only for case (a) — we WILL re-prompt
    // when the user is in case (b) regardless of the flag, because their
    // notifications won't actually arrive without it.
    if (pushPermissionState === 'granted-unsubscribed') {
      setVisible(true);
      return;
    }
    if (pushPermissionState !== 'default') return;

    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem(PROMPT_FLAG) === '1';
    } catch {
      dismissed = false;
    }
    if (dismissed) return;
    setVisible(true);
  }, [pushPermissionState]);

  const remember = (): void => {
    try {
      window.localStorage.setItem(PROMPT_FLAG, '1');
    } catch {
      // private mode — fail open, prompt may show again next visit.
    }
  };

  const handleAllow = async (): Promise<void> => {
    try {
      await subscribeUser();
    } catch {
      // hook stores the error; we just hide the prompt either way so the
      // user isn't stuck if something blew up.
    } finally {
      remember();
      setVisible(false);
    }
  };

  const handleDismiss = (): void => {
    remember();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Enable push notifications"
      className="fixed inset-x-3 z-40 rounded-ss-lg border border-ink-200 dark:border-[#334155] bg-white dark:bg-[#1E293B] p-4 shadow-ss-md animate-ss-slide-up"
      style={{
        // Sit just above the tab bar AND honor the iOS home-indicator inset.
        // 64px tab bar + 12px gap + safe-area inset.
        bottom: 'calc(64px + 12px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      <div className="flex items-start gap-3">
        <div
          aria-hidden="true"
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-ss-md bg-ss-blue/10 text-ss-blue"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink-900 dark:text-[#E2E8F0]">Get notified when it matters</p>
          <p className="mt-1 text-xs leading-snug text-ink-500 dark:text-[#94A3B8]">
            We&rsquo;ll only ping you for callbacks, hot leads, and follow-ups Shelby
            flagged. You can mute anytime in the app.
          </p>
          {error ? (
            <p className="mt-2 text-xs text-red-600">{error.message}</p>
          ) : null}
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                void handleAllow();
              }}
              disabled={isBusy}
              className="flex-1 rounded-ss-md bg-ss-blue px-3 py-2 text-sm font-semibold text-white shadow-ss-sm transition-colors hover:bg-ss-blue-dark disabled:opacity-60"
            >
              {isBusy ? 'Enabling…' : 'Allow notifications'}
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              disabled={isBusy}
              className="rounded-ss-md px-3 py-2 text-sm font-medium text-ink-500 hover:bg-ink-100 disabled:opacity-60"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
